import React, { useState } from 'react'
import { Bell, Loader2, Trash2, RefreshCw, Scale, Globe, CheckCircle2 } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { bff, toClientError } from '../services/bff'
import { useProjects } from '../hooks/useRag'

const run = (p) => p.then((r) => r.data).catch((e) => Promise.reject(toClientError(e)))

// Watches (agent-user-gaps #7): rules over insights. A claim watch re-runs the
// truth lens daily and flags when the corpus consensus MOVES; a topic watch
// re-runs discovery and flags NEW external sources. Manual "check now" too.
export default function WatchesPanel() {
  const qc = useQueryClient()
  const projectsQuery = useProjects()
  const watchesQ = useQuery({
    queryKey: ['watches'],
    queryFn: () => run(bff.get('/api/watches')),
    refetchInterval: 60000,
  })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['watches'] })
  const add = useMutation({ mutationFn: (b) => run(bff.post('/api/watches', b)), onSuccess: invalidate })
  const del = useMutation({ mutationFn: (id) => run(bff.delete(`/api/watches/${id}`)), onSuccess: invalidate })
  const check = useMutation({ mutationFn: (id) => run(bff.post(`/api/watches/${id}/check`, {}, { timeout: 0 })), onSuccess: invalidate })
  const read = useMutation({ mutationFn: (id) => run(bff.post(`/api/watches/${id}/read`)), onSuccess: invalidate })

  const [type, setType] = useState('claim')
  const [query, setQuery] = useState('')
  const [project, setProject] = useState('')
  const watches = watchesQ.data?.watches ?? []

  const submit = () => {
    if (!query.trim()) return
    add.mutate(
      { type, query: query.trim(), project: type === 'claim' ? project : project || undefined },
      { onSuccess: () => setQuery('') }
    )
  }

  return (
    <div className="space-y-4">
      {/* create */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-parchment-border dark:border-midnight-600 p-0.5">
          {[['claim', 'Tvrzení', Scale], ['topic', 'Téma', Globe]].map(([t, label, Icon]) => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs transition ${
                type === t ? 'bg-gold/15 text-gold-deep dark:text-gold' : 'text-ink-soft dark:text-mist/60'
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder={type === 'claim'
            ? 'Tvrzení, jehož konsensus hlídat… (např. „Merkur v retrográdu ovlivňuje techniku")'
            : 'Téma, pro které hlídat nové externí zdroje… (např. „claude code subagents")'}
          className="flex-1 min-w-[280px] h-10 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
        />
        <select
          value={project}
          onChange={(e) => setProject(e.target.value)}
          className="h-10 rounded-lg px-2 text-sm bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
          title={type === 'claim' ? 'Projekt, jehož korpus tvrzení váží (povinné)' : 'Projekt pro AI konsolidaci (volitelné)'}
        >
          <option value="">{type === 'claim' ? 'Projekt…' : '(projekt)'}</option>
          {(projectsQuery.data?.projects ?? []).map((p) => (
            <option key={p.name} value={p.name}>{p.name}</option>
          ))}
        </select>
        <button
          onClick={submit}
          disabled={add.isPending || !query.trim() || (type === 'claim' && !project)}
          className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md text-sm border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-40 dark:text-gold"
        >
          {add.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
          Hlídat
        </button>
      </div>
      {add.isError && <p className="text-sm text-red-600">{add.error?.message}</p>}
      <p className="text-xs text-ink-soft dark:text-mist/50">
        Hlídky se přehodnocují denně (a tlačítkem ↻). Změna — pohyb konsensu u tvrzení,
        nové zdroje u tématu — hlídku zvýrazní, dokud ji neoznačíš za přečtenou.
      </p>

      {watches.length === 0 && !watchesQ.isLoading && (
        <div className="rounded-xl border border-dashed border-parchment-border p-6 text-center text-ink-soft dark:border-midnight-600 dark:text-mist/50">
          <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
          Žádné hlídky. Nastav tvrzení nebo téma a nech platformu hlídat, co se pohne.
        </div>
      )}

      {watches.map((w) => {
        const res = w.last_result ? JSON.parse(w.last_result) : null
        return (
          <div
            key={w.id}
            className={`rounded-xl border px-4 py-3 space-y-1 ${
              w.unread
                ? 'border-gold/60 bg-gold/5'
                : 'border-parchment-border bg-white/60 dark:border-midnight-600 dark:bg-midnight-800/50'
            }`}
          >
            <div className="flex items-center gap-2">
              {w.type === 'claim'
                ? <Scale className="w-4 h-4 shrink-0 text-gold-deep dark:text-gold" />
                : <Globe className="w-4 h-4 shrink-0 text-gold-deep dark:text-gold" />}
              <span className="flex-1 min-w-0 text-sm text-ink dark:text-mist truncate" title={w.query}>
                {w.query}
              </span>
              {w.unread ? (
                <button
                  onClick={() => read.mutate(w.id)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-spark/15 text-spark-600 dark:text-spark"
                  title="Označit za přečtené"
                >
                  ZMĚNA — {w.last_change}
                </button>
              ) : w.last_checked && (
                <span className="text-[11px] text-ink-soft dark:text-mist/50">
                  beze změny · {new Date(w.last_checked).toLocaleDateString()}
                </span>
              )}
              <button
                onClick={() => check.mutate(w.id)}
                disabled={check.isPending}
                className="p-1.5 rounded text-ink-soft hover:text-gold-deep dark:text-mist/50 dark:hover:text-gold"
                title="Vyhodnotit teď"
              >
                {check.isPending && check.variables === w.id
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <RefreshCw className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => del.mutate(w.id)}
                className="p-1.5 rounded text-ink-soft hover:text-red-600 dark:text-mist/50"
                title="Smazat hlídku"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="text-xs text-ink-soft dark:text-mist/50 pl-6">
              {w.project && <span className="mr-3">projekt: {w.project}</span>}
              {res && w.type === 'claim' && (
                <span>konsensus: ✓{res.affirms} ✗{res.contradicts} ±{res.mixed} ·{res.silent}</span>
              )}
              {res && w.type === 'topic' && <span>{res.count} známých zdrojů</span>}
              {!res && <span className="italic">zatím nevyhodnoceno</span>}
            </div>
          </div>
        )
      })}
      {check.isError && <p className="text-sm text-red-600">{check.error?.message}</p>}
    </div>
  )
}
